/*
  File: assets/wu-product-card-duvet.js

  WakeUp duvet product card
  - Keeps product card prices in sync with active collection filters
  - Especially supports size/maat filters
  - Explicitly resets to default variant when size filters are removed
  - Updates price HTML and product links to selected variant
  - Updates sale badge visibility when selected variant changes
  - Re-initializes after Shopify/AJAX facet re-rendering
*/

(function () {
  'use strict';

  var CARD_SELECTOR = '[data-product-card-duvet]';
  var VARIANTS_SELECTOR = '[data-product-variants-json]';
  var PRICE_SELECTOR = '[data-price-wrapper]';
  var LINK_SELECTOR = '[data-product-card-link]';
  var SALE_BADGE_SELECTOR = '[data-sale-badge]';

  var SIZE_KEYWORDS = [
    'maat',
    'size',
    'format',
    'afmeting',
    'afmetingen',
    'dimension',
    'dimensions',
    'product_maat'
  ];

  var CLEAR_FILTER_SELECTORS = [
    '.active-facets__button',
    '.active-facets__button-remove',
    '.facets__reset',
    '.facets__clear',
    '.facet-remove',
    '[data-facets-clear]',
    '[data-clear-filters]',
    '[data-clear-filter]',
    '[href*="filter."]'
  ];

  var debounceTimer = null;
  var lastUrl = window.location.href;

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '')
      .replace(/×/g, 'x')
      .replace(/\*/g, 'x')
      .replace(/-/g, '')
      .replace(/_/g, '')
      .replace(/\//g, '')
      .replace(/cm/g, '')
      .replace(/[^\w]/g, '');
  }

  function humanNormalize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/×/g, 'x')
      .replace(/\*/g, 'x');
  }

  function isSizeLikeName(name) {
    var normalizedName = normalize(name);

    if (!normalizedName) {
      return false;
    }

    return SIZE_KEYWORDS.some(function (keyword) {
      return normalizedName.indexOf(normalize(keyword)) !== -1;
    });
  }

  function parseJson(script) {
    if (!script) {
      return [];
    }

    try {
      var parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function getMoneyFormat() {
    if (window.theme && window.theme.moneyFormat) {
      return window.theme.moneyFormat;
    }

    if (window.theme && window.theme.settings && window.theme.settings.moneyFormat) {
      return window.theme.settings.moneyFormat;
    }

    if (window.Shopify && window.Shopify.money_format) {
      return window.Shopify.money_format;
    }

    return '€{{amount_with_comma_separator}}';
  }

  function formatMoney(cents) {
    var value = Number(cents || 0);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, getMoneyFormat());
    }

    try {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value / 100);
    } catch (error) {
      return '€' + (value / 100).toFixed(2).replace('.', ',');
    }
  }

  function buildPriceHtml(variant) {
    var price = Number(variant && variant.price ? variant.price : 0);
    var compareAtPrice = Number(variant && variant.compare_at_price ? variant.compare_at_price : 0);

    if (compareAtPrice > price) {
      return [
        '<div class="price">',
          '<div class="price__sale">',
            '<span class="price-item price-item--sale">' + formatMoney(price) + '</span>',
            '<span class="price-item price-item--regular">' + formatMoney(compareAtPrice) + '</span>',
          '</div>',
        '</div>'
      ].join('');
    }

    return [
      '<div class="price">',
        '<div class="price__regular">',
          '<span class="price-item price-item--regular-no-sale">' + formatMoney(price) + '</span>',
        '</div>',
      '</div>'
    ].join('');
  }

  function getCurrentUrlParams() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (error) {
      return new URLSearchParams();
    }
  }

  function getCheckedFilterInputs() {
    return Array.prototype.slice.call(
      document.querySelectorAll(
        'input[name^="filter."]:checked, input[name*="filter"]:checked, select[name^="filter."], select[name*="filter"]'
      )
    ).filter(function (input) {
      if (!input.name) {
        return false;
      }

      if (input.disabled) {
        return false;
      }

      if (input.tagName && input.tagName.toLowerCase() === 'select') {
        return input.value !== '';
      }

      return true;
    });
  }

  function addValue(list, value) {
    var raw = String(value || '').trim();

    if (!raw) {
      return;
    }

    var parts = raw.split(',');

    parts.forEach(function (part) {
      var cleaned = String(part || '').trim();

      if (!cleaned) {
        return;
      }

      var normalized = normalize(cleaned);

      if (normalized && list.indexOf(normalized) === -1) {
        list.push(normalized);
      }
    });
  }

  function getInputLabelText(input) {
    if (!input) {
      return '';
    }

    var label = null;

    if (input.id && window.CSS && typeof window.CSS.escape === 'function') {
      try {
        label = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
      } catch (error) {
        label = null;
      }
    }

    if (!label && input.id) {
      try {
        label = document.querySelector('label[for="' + input.id.replace(/"/g, '\\"') + '"]');
      } catch (errorFallback) {
        label = null;
      }
    }

    if (!label && input.closest) {
      label = input.closest('label');
    }

    return label ? label.textContent || '' : '';
  }

  function getActiveSizeValuesFromUrl() {
    var values = [];
    var params = getCurrentUrlParams();

    params.forEach(function (value, key) {
      if (isSizeLikeName(key)) {
        addValue(values, value);
      }
    });

    return values;
  }

  function getActiveSizeValuesFromInputs() {
    var values = [];

    getCheckedFilterInputs().forEach(function (input) {
      var name = input.name || '';
      var value = input.value || '';

      if (isSizeLikeName(name)) {
        addValue(values, value);
      }

      var labelText = getInputLabelText(input);

      if (isSizeLikeName(labelText)) {
        addValue(values, value);
      }
    });

    return values;
  }

  function mergeUniqueValues(primary, secondary) {
    var merged = [];

    primary.concat(secondary).forEach(function (value) {
      if (value && merged.indexOf(value) === -1) {
        merged.push(value);
      }
    });

    return merged;
  }

  function getActiveSizeValues() {
    var urlValues = getActiveSizeValuesFromUrl();
    var inputValues = getActiveSizeValuesFromInputs();

    return mergeUniqueValues(urlValues, inputValues);
  }

  function hasActiveSizeFilter() {
    return getActiveSizeValues().length > 0;
  }

  function variantSearchString(variant) {
    if (!variant) {
      return '';
    }

    return [
      variant.title,
      variant.option1,
      variant.option2,
      variant.option3,
      variant.product_maat
    ].map(function (value) {
      return normalize(value);
    }).join('|');
  }

  function variantHumanSearchString(variant) {
    if (!variant) {
      return '';
    }

    return [
      variant.title,
      variant.option1,
      variant.option2,
      variant.option3,
      variant.product_maat
    ].map(function (value) {
      return humanNormalize(value);
    }).join('|');
  }

  function variantMatchesSize(variant, selectedSizes) {
    if (!selectedSizes || !selectedSizes.length) {
      return false;
    }

    var normalizedSearch = variantSearchString(variant);
    var humanSearch = variantHumanSearchString(variant);

    return selectedSizes.some(function (selectedSize) {
      if (!selectedSize) {
        return false;
      }

      if (normalizedSearch.indexOf(selectedSize) !== -1) {
        return true;
      }

      var humanSelected = humanNormalize(selectedSize);

      if (humanSelected && humanSearch.indexOf(humanSelected) !== -1) {
        return true;
      }

      return false;
    });
  }

  function getDefaultVariant(card, variants) {
    if (!variants || !variants.length) {
      return null;
    }

    var defaultVariantId = String(card.getAttribute('data-default-variant-id') || '');

    if (defaultVariantId) {
      var matched = variants.find(function (variant) {
        return String(variant.id) === defaultVariantId;
      });

      if (matched) {
        return matched;
      }
    }

    return variants[0];
  }

  function getBestVariant(card, variants) {
    if (!variants || !variants.length) {
      return null;
    }

    var selectedSizes = getActiveSizeValues();

    /*
      Important reset logic:
      When no active size filters exist anymore, always return the default variant.
      This prevents cards from keeping the last filtered size price after filters are cleared.
    */
    if (!selectedSizes.length) {
      return getDefaultVariant(card, variants);
    }

    var availableMatch = variants.find(function (variant) {
      return variant.available && variantMatchesSize(variant, selectedSizes);
    });

    if (availableMatch) {
      return availableMatch;
    }

    var anyMatch = variants.find(function (variant) {
      return variantMatchesSize(variant, selectedSizes);
    });

    if (anyMatch) {
      return anyMatch;
    }

    return getDefaultVariant(card, variants);
  }

  function buildVariantUrl(baseUrl, variantId) {
    if (!baseUrl) {
      return '#';
    }

    try {
      var url = new URL(baseUrl, window.location.origin);

      if (variantId) {
        url.searchParams.set('variant', String(variantId));
      }

      return url.pathname + url.search + url.hash;
    } catch (error) {
      var cleanBase = String(baseUrl).split('?')[0];

      if (variantId) {
        return cleanBase + '?variant=' + encodeURIComponent(String(variantId));
      }

      return cleanBase;
    }
  }

  function updateSaleBadge(card, variant) {
    var saleBadge = card.querySelector(SALE_BADGE_SELECTOR);

    if (!saleBadge || !variant) {
      return;
    }

    var price = Number(variant.price || 0);
    var compareAtPrice = Number(variant.compare_at_price || 0);
    var hasSale = compareAtPrice > price;

    if (hasSale) {
      saleBadge.classList.remove('product-card-duvet__sale-badge--hidden');
      saleBadge.setAttribute('aria-hidden', 'false');
    } else {
      saleBadge.classList.add('product-card-duvet__sale-badge--hidden');
      saleBadge.setAttribute('aria-hidden', 'true');
    }
  }

  function updateCard(card, forceDefault) {
    if (!card) {
      return;
    }

    var variantsScript = card.querySelector(VARIANTS_SELECTOR);
    var variants = parseJson(variantsScript);

    if (!variants.length) {
      return;
    }

    var selectedVariant = forceDefault
      ? getDefaultVariant(card, variants)
      : getBestVariant(card, variants);

    if (!selectedVariant) {
      return;
    }

    var priceWrapper = card.querySelector(PRICE_SELECTOR);

    if (priceWrapper) {
      priceWrapper.innerHTML = buildPriceHtml(selectedVariant);
    }

    updateSaleBadge(card, selectedVariant);

    var baseUrl = card.getAttribute('data-product-url') || '';
    var selectedUrl = buildVariantUrl(baseUrl, selectedVariant.id);
    var links = card.querySelectorAll(LINK_SELECTOR);

    links.forEach(function (link) {
      link.setAttribute('href', selectedUrl);
    });

    card.setAttribute('data-current-variant-id', String(selectedVariant.id));
  }

  function initCards(root, forceDefault) {
    var scope = root || document;
    var cards = scope.querySelectorAll ? scope.querySelectorAll(CARD_SELECTOR) : [];

    cards.forEach(function (card) {
      updateCard(card, forceDefault);
    });
  }

  function resetCardsToDefault() {
    initCards(document, true);
  }

  function scheduleUpdate(forceDefault) {
    window.clearTimeout(debounceTimer);

    debounceTimer = window.setTimeout(function () {
      if (forceDefault || !hasActiveSizeFilter()) {
        resetCardsToDefault();
      } else {
        initCards(document, false);
      }
    }, 80);
  }

  function scheduleMultipleUpdates(forceDefault) {
    window.setTimeout(function () {
      scheduleUpdate(forceDefault);
    }, 80);

    window.setTimeout(function () {
      scheduleUpdate(forceDefault);
    }, 250);

    window.setTimeout(function () {
      scheduleUpdate(forceDefault);
    }, 600);

    window.setTimeout(function () {
      scheduleUpdate(forceDefault);
    }, 1000);
  }

  function targetMatchesClearFilter(target) {
    if (!target || !target.closest) {
      return false;
    }

    return CLEAR_FILTER_SELECTORS.some(function (selector) {
      try {
        return !!target.closest(selector);
      } catch (error) {
        return false;
      }
    });
  }

  function checkUrlChanged() {
    if (window.location.href === lastUrl) {
      return false;
    }

    lastUrl = window.location.href;
    return true;
  }

  function bindHistoryEvents() {
    if (!window.history) {
      return;
    }

    var originalPushState = window.history.pushState;
    var originalReplaceState = window.history.replaceState;

    if (typeof originalPushState === 'function') {
      window.history.pushState = function () {
        var result = originalPushState.apply(this, arguments);

        window.setTimeout(function () {
          if (checkUrlChanged()) {
            scheduleMultipleUpdates(!hasActiveSizeFilter());
          }
        }, 0);

        return result;
      };
    }

    if (typeof originalReplaceState === 'function') {
      window.history.replaceState = function () {
        var result = originalReplaceState.apply(this, arguments);

        window.setTimeout(function () {
          if (checkUrlChanged()) {
            scheduleMultipleUpdates(!hasActiveSizeFilter());
          }
        }, 0);

        return result;
      };
    }

    window.addEventListener('popstate', function () {
      window.setTimeout(function () {
        checkUrlChanged();
        scheduleMultipleUpdates(!hasActiveSizeFilter());
      }, 0);
    });
  }

  function bindEvents() {
    document.addEventListener('change', function (event) {
      var target = event.target;

      if (!target || !target.name) {
        return;
      }

      if (target.name.indexOf('filter') !== -1) {
        scheduleMultipleUpdates(!hasActiveSizeFilter());
      }
    }, true);

    document.addEventListener('click', function (event) {
      var target = event.target;

      if (!target || !target.closest) {
        return;
      }

      if (targetMatchesClearFilter(target)) {
        /*
          Clear chips often update the URL/DOM asynchronously.
          Run several delayed checks and force default at first,
          then later checks will re-apply filtered variant if a size filter still exists.
        */
        scheduleMultipleUpdates(true);
        return;
      }

      if (
        target.closest('[data-filter]') ||
        target.closest('[data-facet]') ||
        target.closest('facet-filters-form') ||
        target.closest('.facets') ||
        target.closest('.facets__form') ||
        target.closest('.active-facets')
      ) {
        scheduleMultipleUpdates(!hasActiveSizeFilter());
      }
    }, true);

    [
      'shopify:section:load',
      'shopify:section:reorder',
      'facets:updated',
      'facet:updated',
      'filters:updated',
      'collection:updated',
      'products:updated'
    ].forEach(function (eventName) {
      document.addEventListener(eventName, function () {
        scheduleMultipleUpdates(!hasActiveSizeFilter());
      });
    });
  }

  function bindMutationObserver() {
    if (!window.MutationObserver) {
      return;
    }

    var observer = new MutationObserver(function (mutations) {
      var shouldUpdate = false;
      var filtersChanged = false;

      mutations.forEach(function (mutation) {
        if (mutation.target && mutation.target.nodeType === 1) {
          var target = mutation.target;

          if (
            target.matches &&
            (
              target.matches('.facets') ||
              target.matches('.active-facets') ||
              target.matches('facet-filters-form') ||
              target.matches('#product-grid') ||
              target.matches('.products')
            )
          ) {
            filtersChanged = true;
          }
        }

        if (!mutation.addedNodes || !mutation.addedNodes.length) {
          return;
        }

        Array.prototype.slice.call(mutation.addedNodes).forEach(function (node) {
          if (!node || node.nodeType !== 1) {
            return;
          }

          if (
            (node.matches && node.matches(CARD_SELECTOR)) ||
            (node.querySelector && node.querySelector(CARD_SELECTOR))
          ) {
            shouldUpdate = true;
          }

          if (
            (node.matches && (
              node.matches('.facets') ||
              node.matches('.active-facets') ||
              node.matches('facet-filters-form')
            )) ||
            (node.querySelector && (
              node.querySelector('.facets') ||
              node.querySelector('.active-facets') ||
              node.querySelector('facet-filters-form')
            ))
          ) {
            filtersChanged = true;
          }
        });
      });

      if (shouldUpdate || filtersChanged || checkUrlChanged()) {
        scheduleMultipleUpdates(!hasActiveSizeFilter());
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    bindHistoryEvents();
    bindEvents();
    bindMutationObserver();

    if (!hasActiveSizeFilter()) {
      resetCardsToDefault();
    } else {
      initCards(document, false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();